import { hubPageBg } from './accountHubStyles';
import { downloadCodes } from './accountHubUtils';
import { FC, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAsUserParam } from './useAsUserParam';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyIcon from '@mui/icons-material/Key';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { setLoginSecurity } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import axios from 'src/utils/axios';

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
  p: 2,
};

const BackupCodesPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const asUser = useAsUserParam();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}`;

  const loginSecurity = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.loginSecurity
  );

  const [generating, setGenerating] = useState(false);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = useCallback(async (): Promise<void> => {
    setGenerating(true);
    setError(null);
    try {
      const resp = await axios.post(
        `v2/user/totp/recovery-codes/regenerate/${asUser}`
      );
      const codes =
        (resp.data as { recovery_codes?: string[] }).recovery_codes ?? [];
      setNewCodes(codes);
      dispatch(
        setLoginSecurity({
          backup_codes_remaining: codes.length,
          backup_codes_generated_at: new Date().toISOString(),
        })
      );
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? 'Could not generate backup codes.';
      setError(msg);
    } finally {
      setGenerating(false);
    }
  }, [dispatch, asUser]);
  const generatedAt = loginSecurity.backup_codes_generated_at
    ? new Date(loginSecurity.backup_codes_generated_at).toLocaleDateString(
        'en-US',
        {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }
      )
    : null;

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Backup Codes"
        icon={<KeyIcon />}
        backRoute={newCodes ? undefined : backRoute}
        subtitle="Generate new recovery codes"
      />
      <ProxyBanner />

      <Stack gap={2.5} sx={{ p: 3 }}>
        {!newCodes ? (
          <>
            {/* Warning banner — warning.lighter bg per mockup */}
            <Box
              sx={{
                backgroundColor: 'warning.lighter',
                borderRadius: '12px',
                p: 2,
              }}
            >
              <Stack direction="row" gap={1} alignItems="flex-start">
                <WarningAmberIcon
                  sx={{
                    fontSize: 18,
                    color: 'warning.dark',
                    mt: 0.2,
                    flexShrink: 0,
                  }}
                />
                <Stack gap={0.5}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: 'warning.darker',
                    }}
                  >
                    Important
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: 'warning.darker',
                      lineHeight: 1.5,
                    }}
                  >
                    Generating new backup codes will invalidate all existing
                    codes. Make sure you&apos;ve saved your current codes or no
                    longer need them.
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Current codes info card */}
            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
                Current Backup Codes
              </Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontSize: 14 }}>
                  {loginSecurity.backup_codes_remaining} of 10 codes remaining
                </Typography>
                {generatedAt && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    Generated: {generatedAt}
                  </Typography>
                )}
              </Stack>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {/* Generate button — full width green */}
            <Button
              variant="contained"
              fullWidth
              onClick={() => void handleGenerate()}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={16} /> : null}
              sx={{
                borderRadius: '12px',
                backgroundColor: 'primary.dark',
                fontWeight: 600,
                py: 1.5,
                '&:hover': { backgroundColor: 'primary.darker' },
              }}
            >
              <span>
                {generating ? 'Generating…' : 'Generate New Backup Codes'}
              </span>
            </Button>

            {/* Cancel — plain text, centred */}
            <Typography
              onClick={() => navigate(backRoute)}
              sx={{
                fontSize: 14,
                color: 'text.secondary',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Cancel
            </Typography>
          </>
        ) : (
          /* Show new codes — displayed once */
          <>
            <Alert severity="success">
              New backup codes generated successfully!
            </Alert>

            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                Your new backup codes
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
                Each code can only be used once. Store them somewhere safe.
              </Typography>
              <Box
                sx={{
                  backgroundColor: 'action.hover',
                  borderRadius: '8px',
                  p: 1.5,
                  mb: 2,
                }}
              >
                {newCodes.map((c) => (
                  <Typography
                    key={c}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 14,
                      lineHeight: 1.8,
                    }}
                  >
                    {c}
                  </Typography>
                ))}
              </Box>
              <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12 }}>
                  These codes are shown <strong>only once</strong>. Download or
                  write them down.
                </Typography>
              </Alert>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                onClick={() => downloadCodes(newCodes)}
                sx={{ mb: 2, borderRadius: '8px' }}
              >
                Download
              </Button>
              <Stack direction="row" alignItems="center" gap={1}>
                <input
                  type="checkbox"
                  id="ack-backup"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <label htmlFor="ack-backup">
                  <Typography sx={{ fontSize: 14 }}>
                    I have saved my backup codes in a safe place
                  </Typography>
                </label>
              </Stack>
            </Box>

            <Button
              variant="contained"
              fullWidth
              disabled={!acknowledged}
              onClick={() => navigate(backRoute, { replace: true })}
              sx={{
                borderRadius: '12px',
                backgroundColor: 'primary.dark',
                fontWeight: 600,
                py: 1.5,
                '&:hover': { backgroundColor: 'primary.darker' },
              }}
            >
              Done
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default BackupCodesPage;
